import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';

interface IUseMonitorFullscreenArgs {
  /** The replay bar owns Esc while it is open: one press closes it, the next exits. */
  replayOpen: boolean;
  /** So does the floor drawer. */
  drawerOpen: boolean;
}

/** The Monitor's fullscreen mode. The toggle moves between the card and the top bar, so focus follows it
 *  (after the app under it is reachable again). */
export function useMonitorFullscreen({ replayOpen, drawerOpen }: IUseMonitorFullscreenArgs) {
  const [fullscreen, setFullscreen] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const toggle = useCallback(() => setFullscreen((current) => !current), []);
  const toggleRail = useCallback(() => setRailCollapsed((current) => !current), []);

  const onKeyDown = useEffectEvent((event: KeyboardEvent) => {
    if (event.key !== 'Escape' || event.defaultPrevented || replayOpen || drawerOpen) return;
    setFullscreen(false);
  });
  const wasFullscreen = useRef(false);
  useEffect(() => {
    if (wasFullscreen.current !== fullscreen) toggleRef.current?.focus();
    wasFullscreen.current = fullscreen;
    if (!fullscreen) return;
    // The app under the fullscreen page (portalled beside it) stays out of reach of Tab and screen readers.
    const app = document.getElementById('root');
    app?.toggleAttribute('inert', true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      app?.toggleAttribute('inert', false);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [fullscreen]);

  return { fullscreen, toggle, toggleRef, railCollapsed, toggleRail };
}
